import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const STATUS_CLS = {
  Open: "bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/30",
  "Under Review": "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40",
  Resolved: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30",
};

function OutcomeRow({ r, onSaved }) {
  const [status, setStatus] = useState(r.status || "Open");
  const [resolution, setResolution] = useState(r.resolution || "");
  const [preventable, setPreventable] = useState(r.preventable || "");
  const [receivedBy, setReceivedBy] = useState(r.received_by || "");
  const [busy, setBusy] = useState(false);
  const dirty = status !== (r.status || "Open") || resolution !== (r.resolution || "") || preventable !== (r.preventable || "") || receivedBy !== (r.received_by || "");
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${r.client_id}/incidents/${r.id}`, { status, resolution, preventable, received_by: receivedBy });
      toast.success(`${r.client_name}: saved`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-4 py-3" data-testid={`outcome-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-stone-500">{r.date}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.kind === "complaint" ? "bg-[#B7791F]/10 text-[#8a5d14]" : "bg-[#C0392B]/10 text-[#C0392B]"}`}>{r.category || r.kind}</span>
        <span className="text-sm font-semibold text-stone-900">{r.client_name}</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="ml-auto h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`outcome-status-${r.id}`}>
          {["Open", "Under Review", "Resolved"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <p className="mt-1 text-xs text-stone-600">{r.description}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}
          placeholder="Taken by (office) — who received this report"
          className="h-8 w-52 rounded-md border border-stone-300 px-2 text-xs" data-testid={`outcome-takenby-${r.id}`} />
        <input value={resolution} onChange={(e) => setResolution(e.target.value)}
          placeholder="Outcome — e.g. ER visit, released same night, care plan updated"
          className="h-8 flex-1 rounded-md border border-stone-300 px-2 text-xs" data-testid={`outcome-resolution-${r.id}`} />
        <select value={preventable} onChange={(e) => setPreventable(e.target.value)}
          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`outcome-preventable-${r.id}`}>
          <option value="">Preventable? —</option>
          <option value="Yes">Preventable: yes</option>
          <option value="No">Preventable: no</option>
          <option value="Uncertain">Uncertain</option>
        </select>
        <Button onClick={save} disabled={busy || !dirty} size="sm"
          className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`outcome-save-${r.id}`}>
          {busy ? "…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default function IncidentLogPage() {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("incident");
  const [outcomeMode, setOutcomeMode] = useState(false);
  const [prevOnly, setPrevOnly] = useState(false);

  const load = () => {
    api.get("/admin/incident-log").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/incident-log/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "incident-log.csv";
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printLog = async () => {
    const w = window.open("", "_blank");  // sync open — async opens get popup-blocked
    try {
      const r = await api.get("/admin/incident-log/print.pdf", { responseType: "blob" });
      if (w) w.location.href = URL.createObjectURL(r.data);
    } catch (e) { if (w) w.close(); toast.error(formatApiError(e)); }
  };

  const rows = (data?.incidents || [])
    .filter((r) => (kind === "all" ? true : r.kind === kind))
    .filter((r) => (prevOnly ? r.preventable === "Yes" : true));
  const openRows = rows.filter((r) => r.status !== "Resolved");

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="incident-log-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Incident log</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">
            Every incident across all clients, newest first — including the ones caregivers flag on their
            wrap-ups. Status and resolution are worked on the client's own Incidents &amp; Complaints page,
            where a serious one can be filed as a formal report. Records are kept 7 years.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant={outcomeMode ? "default" : "outline"} onClick={() => setOutcomeMode(!outcomeMode)}
            className={`rounded-full ${outcomeMode ? "bg-primary text-primary-foreground" : ""}`} data-testid="incident-log-outcome-mode">
            {outcomeMode ? "Done entering outcomes" : "Enter outcomes (bulk)"}
          </Button>
          <Button variant="outline" onClick={printLog} className="rounded-full" data-testid="incident-log-print">Print log</Button>
          <Button variant="outline" onClick={exportCsv} className="rounded-full" data-testid="incident-log-export">Export CSV</Button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="incident-log-summary">
        {[["incident", "Incidents"], ["complaint", "Complaints (client-filed)"], ["all", "All"]].map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${kind === k ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500"}`}
            data-testid={`incident-log-filter-${k}`}>{label}</button>
        ))}
        <span className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-semibold text-stone-600" data-testid="incident-log-open-count">
          {openRows.length} open of {rows.length}
        </span>
        <button onClick={() => setPrevOnly((v) => !v)}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${prevOnly ? "border-[#C0392B] bg-[#C0392B]/10 text-[#C0392B]" : "border-stone-200 bg-white text-stone-600"}`}
          data-testid="incident-log-preventable-count" title="Click to see only preventable incidents">
          preventable: {(data?.incidents || []).filter((r) => (kind === "all" ? true : r.kind === kind) && r.preventable === "Yes").length} yes · {(data?.incidents || []).filter((r) => (kind === "all" ? true : r.kind === kind) && r.preventable === "Uncertain").length} uncertain · {(data?.incidents || []).filter((r) => (kind === "all" ? true : r.kind === kind) && !r.preventable).length} not marked
        </button>
      </div>

      <div className="mt-6 space-y-2">
        {data === null ? (
          <p className="text-sm text-stone-400">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-10 text-center" data-testid="incident-log-empty">
            <p className="text-sm font-semibold text-stone-600">Nothing logged here yet</p>
            <p className="mt-1 text-xs text-stone-400">File from a client's page (Incidents &amp; Complaints) or let caregiver wrap-ups bring them in automatically.</p>
          </div>
        ) : outcomeMode ? (
          rows.map((r) => <OutcomeRow key={r.id} r={r} onSaved={load} />)
        ) : (
          rows.map((r) => (
            <Link key={r.id} to={`/clients/${r.client_id}/incidents`}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 transition-colors hover:border-primary/40"
              data-testid={`incident-row-${r.id}`}>
              <span className="text-xs font-semibold text-stone-500">{r.date}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.kind === "complaint" ? "bg-[#B7791F]/10 text-[#8a5d14]" : "bg-[#C0392B]/10 text-[#C0392B]"}`}>
                {r.category || r.kind}
              </span>
              <span className="text-sm font-semibold text-stone-900">{r.client_name}</span>
              {r.received_by && <span className="text-[10px] text-stone-400">taken by {r.received_by}</span>}
              <span className="min-w-0 flex-1 truncate text-xs text-stone-600">{r.description}</span>
              {r.source === "wrap_up" && (
                <span className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#8a5d14]">auto · wrap-up</span>
              )}
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[r.status] || STATUS_CLS.Open}`} data-testid={`incident-status-${r.id}`}>
                {r.status || "Open"}
              </span>
              <ArrowRight size={13} className="text-stone-300" />
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
